import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { statsApi, promemoriaApi, exchangeRatesApi, speseApi, subscriptionApi } from '../lib/api';
import { ImageLightbox } from '../components/ImageLightbox';
import { DashboardStatsSkeleton, MotoCardSkeleton, PromemoriaCardSkeleton, Skeleton } from '../components/Skeleton';
import { 
  Bike, 
  Gauge, 
  Map, 
  Wallet, 
  Bell, 
  Wrench,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Camera,
  ChevronDown,
  ZoomIn,
  Fuel,
  Plus,
  Crown,
  CreditCard
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { it } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VALUTE = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CHF', symbol: 'CHF', name: 'Franco Svizzero' },
  { code: 'USD', symbol: '$', name: 'Dollaro USA' },
];

export default function Dashboard() {
  const { selectedMotoId, getSelectedMoto, motoList } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [promemoria, setPromemoria] = useState([]);
  const [subscriptionReminders, setSubscriptionReminders] = useState([]);
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState('CHF');
  const [exchangeRates, setExchangeRates] = useState({});
  const [spese, setSpese] = useState([]);
  const [lastRefuel, setLastRefuel] = useState(null);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const selectedMoto = getSelectedMoto();

  useEffect(() => {
    loadData();
  }, [selectedMotoId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, promRes, ratesRes, speseRes, renewalRes, trialRes] = await Promise.all([
        statsApi.getStats(selectedMotoId),
        promemoriaApi.getAll(selectedMotoId),
        exchangeRatesApi.getRates(),
        speseApi.getAll(selectedMotoId),
        subscriptionApi.getRenewalReminders().catch(() => ({ data: { reminders: [] } })),
        subscriptionApi.getTrialStatus().catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setPromemoria(promRes.data.filter(p => !p.completato).slice(0, 5));
      setExchangeRates(ratesRes.data.rates || {});
      setSpese(speseRes.data);
      setSubscriptionReminders(renewalRes.data?.reminders || []);
      setTrialStatus(trialRes.data);
      
      // Find last refuel
      const refuels = speseRes.data.filter(s => s.categoria?.toLowerCase() === 'carburante');
      if (refuels.length > 0) {
        // Sort by date descending (DD.MM.YYYY format)
        const sorted = refuels.sort((a, b) => {
          const dateA = a.data?.split('.').reverse().join('') || '';
          const dateB = b.data?.split('.').reverse().join('') || '';
          return dateB.localeCompare(dateA);
        });
        setLastRefuel(sorted[0]);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Converti importo da una valuta a EUR
  const convertToEur = (importo, fromCurrency) => {
    if (!fromCurrency || fromCurrency === 'EUR') return importo;
    const rate = exchangeRates[fromCurrency];
    if (!rate) return importo;
    return importo * rate;
  };

  // Converti da EUR a valuta target
  const convertFromEur = (eurAmount, toCurrency) => {
    if (!toCurrency || toCurrency === 'EUR') return eurAmount;
    const rate = exchangeRates[toCurrency];
    if (!rate || rate === 0) return eurAmount;
    return eurAmount / rate;
  };

  // Calcola totale spese nella valuta selezionata
  const getTotalInCurrency = () => {
    // Prima converti tutte le spese in EUR
    const totalEur = spese.reduce((total, s) => {
      const valuta = s.valuta || 'EUR';
      return total + convertToEur(s.importo || 0, valuta);
    }, 0);
    // Poi converti da EUR alla valuta selezionata
    return convertFromEur(totalEur, selectedCurrency);
  };

  const formatCurrency = (value, currency = 'EUR') => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('it-IT').format(value || 0);
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('.')) {
      const [day, month, year] = dateStr.split('.');
      return new Date(year, month - 1, day);
    }
    return parseISO(dateStr);
  };

  const getDaysUntil = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return null;
    return differenceInDays(date, new Date());
  };

  const getMotoImageUrl = (moto) => {
    if (moto?.foto_urls && moto.foto_urls.length > 0) {
      const url = moto.foto_urls[0];
      return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
    }
    return null;
  };

  const getImageUrl = (url) => {
    return url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container" data-testid="dashboard-page">
          <div className="page-header">
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Caricamento...</p>
          </div>
          
          {/* Skeleton for hero section */}
          <div className="vault-card mb-8">
            <div className="flex flex-col md:flex-row gap-6">
              <Skeleton className="w-full md:w-80 h-48" />
              <div className="flex-1">
                <Skeleton className="h-8 w-48 mb-2" />
                <Skeleton className="h-5 w-24 mb-4" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-[#262626]">
                  {[...Array(4)].map((_, i) => (
                    <div key={i}>
                      <Skeleton className="h-3 w-20 mb-2" />
                      <Skeleton className="h-7 w-16" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Skeleton for stats grid */}
          <DashboardStatsSkeleton />
          
          {/* Skeleton for content */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="vault-card">
              <Skeleton className="h-6 w-32 mb-6" />
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <PromemoriaCardSkeleton key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="dashboard-page">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {selectedMoto 
              ? `${selectedMoto.marca} ${selectedMoto.modello}` 
              : 'Panoramica di tutte le moto'}
          </p>
        </div>

        {/* Selected Moto Hero Section */}
        {selectedMoto && (
          <div className="vault-card mb-8 overflow-hidden" data-testid="selected-moto-hero">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Moto Image Carousel */}
              <div className="w-full md:w-80 h-48 md:h-auto flex-shrink-0 bg-[#121212] border border-[#262626] relative group">
                {selectedMoto.foto_urls && selectedMoto.foto_urls.length > 0 ? (
                  <>
                    <img 
                      src={getImageUrl(selectedMoto.foto_urls[currentPhotoIndex])} 
                      alt={`${selectedMoto.marca} ${selectedMoto.modello}`}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setLightboxOpen(true)}
                      data-testid="selected-moto-image"
                    />
                    {/* Zoom icon overlay */}
                    <div 
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                      onClick={() => setLightboxOpen(true)}
                    >
                      <ZoomIn className="w-8 h-8 text-white" />
                    </div>
                    {/* Navigation arrows */}
                    {selectedMoto.foto_urls.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setCurrentPhotoIndex(prev => prev === 0 ? selectedMoto.foto_urls.length - 1 : prev - 1); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-1 bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FF5F1F]"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setCurrentPhotoIndex(prev => prev === selectedMoto.foto_urls.length - 1 ? 0 : prev + 1); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FF5F1F]"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        {/* Dots indicator */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {selectedMoto.foto_urls.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => { e.stopPropagation(); setCurrentPhotoIndex(idx); }}
                              className={`w-2 h-2 rounded-full transition-colors ${idx === currentPhotoIndex ? 'bg-[#FF5F1F]' : 'bg-white/50 hover:bg-white'}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#4B5563]">
                    <Camera className="w-12 h-12 mb-2" />
                    <p className="text-sm">Nessuna foto</p>
                  </div>
                )}
              </div>

              {/* Moto Info */}
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="font-['Teko'] text-3xl text-white uppercase tracking-tight">
                      {selectedMoto.marca} {selectedMoto.modello}
                    </h2>
                    <span className="vault-badge vault-badge-orange">{selectedMoto.anno}</span>
                  </div>
                  <p className="text-[#9CA3AF] font-mono text-lg mb-4">{selectedMoto.targa}</p>
                  {selectedMoto.note && (
                    <p className="text-[#9CA3AF] text-sm">{selectedMoto.note}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-[#262626]">
                  <div>
                    <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Km Attuali</p>
                    <p className="text-[#FF5F1F] font-mono text-2xl">{formatNumber(selectedMoto.km_attuali)}</p>
                  </div>
                  <div>
                    <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Viaggi</p>
                    <p className="text-white font-['Teko'] text-2xl">{stats?.totale_viaggi || 0}</p>
                  </div>
                  <div>
                    <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Spese Totali</p>
                    <p className="text-white font-mono text-lg">{formatCurrency(getTotalInCurrency(), selectedCurrency)}</p>
                  </div>
                  <div>
                    <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Ultima Manut.</p>
                    <p className="text-white font-mono text-lg">{stats?.ultima_manutenzione || '—'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className={`grid grid-cols-2 lg:grid-cols-3 ${selectedMoto ? 'xl:grid-cols-5' : 'xl:grid-cols-6'} gap-4 mb-8`}>
          {!selectedMoto && (
            <div className="list-item" style={{ animationDelay: '0.05s' }}>
              <StatCard 
                icon={Bike} 
                label="Moto" 
                value={stats?.totale_moto || 0}
                link="/moto"
              />
            </div>
          )}
          <div className="list-item" style={{ animationDelay: '0.1s' }}>
            <StatCard 
              icon={Gauge} 
              label="Km Totali" 
              value={formatNumber(stats?.totale_km)}
              mono
            />
          </div>
          <div className="list-item" style={{ animationDelay: '0.15s' }}>
            <StatCard 
              icon={Map} 
              label="Viaggi" 
              value={stats?.totale_viaggi || 0}
              link="/viaggi"
            />
          </div>
          
          {/* Spese Totali con selettore valuta */}
          <div className="stat-card vault-card relative list-item" style={{ animationDelay: '0.2s' }}>
            <Link to="/spese" className="block">
              <div className="flex items-center gap-3 mb-3">
                <Wallet className="w-5 h-5 text-[#9CA3AF]" />
                <span className="text-[#9CA3AF] text-xs uppercase tracking-wider">Spese Totali</span>
              </div>
              <p className="text-white text-2xl font-semibold font-mono">
                {formatCurrency(getTotalInCurrency(), selectedCurrency)}
              </p>
            </Link>
            {/* Currency Selector */}
            <div className="absolute top-2 right-2">
              <button
                onClick={(e) => { e.preventDefault(); setCurrencyDropdownOpen(!currencyDropdownOpen); }}
                className="flex items-center gap-1 px-2 py-1 bg-[#262626] hover:bg-[#333] text-[#9CA3AF] text-xs rounded transition-colors"
                data-testid="currency-selector-btn"
              >
                {VALUTE.find(v => v.code === selectedCurrency)?.symbol} {selectedCurrency}
                <ChevronDown className={`w-3 h-3 transition-transform ${currencyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {currencyDropdownOpen && (
                <div className="absolute top-full right-0 mt-1 bg-[#0a0a0a] border border-[#262626] z-50 min-w-[100px]">
                  {VALUTE.map((v) => (
                    <button
                      key={v.code}
                      onClick={(e) => { 
                        e.preventDefault(); 
                        setSelectedCurrency(v.code); 
                        setCurrencyDropdownOpen(false); 
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-[#FF5F1F]/10 transition-colors ${
                        selectedCurrency === v.code ? 'text-[#FF5F1F] bg-[#FF5F1F]/10' : 'text-white'
                      }`}
                      data-testid={`currency-option-${v.code}`}
                    >
                      {v.symbol} {v.code}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="list-item" style={{ animationDelay: '0.25s' }}>
            <StatCard 
              icon={Bell} 
              label="Promemoria" 
              value={stats?.promemoria_attivi || 0}
              link="/promemoria"
              alert={stats?.promemoria_attivi > 0}
            />
          </div>
          <div className="list-item" style={{ animationDelay: '0.3s' }}>
            <StatCard 
              icon={Wrench} 
              label="Ultima Manutenzione" 
              value={stats?.ultima_manutenzione || '—'}
              mono
              link="/manutenzione"
            />
          </div>
        </div>

        {/* Quick Actions */}
        {selectedMoto && (
          <div className="flex gap-3 mb-8 flex-wrap">
            <Link 
              to="/spese?action=fuel"
              className="flex items-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white transition-colors"
              data-testid="quick-fuel-btn"
            >
              <Fuel className="w-5 h-5" />
              <span className="font-semibold">Aggiungi Rifornimento</span>
            </Link>
            <Link 
              to="/spese"
              className="flex items-center gap-2 px-4 py-3 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white transition-colors"
              data-testid="quick-expense-btn"
            >
              <Plus className="w-5 h-5" />
              <span className="font-semibold">Nuova Spesa</span>
            </Link>
          </div>
        )}

        {/* Last Refuel Widget */}
        {lastRefuel && selectedMoto && (
          <div className="vault-card mb-8 bg-gradient-to-r from-green-900/20 to-green-800/10 border-green-600/30" data-testid="last-refuel-widget">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-green-600/20 rounded-full flex items-center justify-center">
                  <Fuel className="w-7 h-7 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Ultimo Rifornimento</h3>
                  <p className="text-[#9CA3AF] text-sm">
                    {lastRefuel.data} • {lastRefuel.litri ? `${lastRefuel.litri} L` : lastRefuel.descrizione}
                  </p>
                  {lastRefuel.km_contachilometri && selectedMoto.km_attuali && (
                    <p className="text-green-400 text-sm font-mono mt-1">
                      {formatNumber(selectedMoto.km_attuali - lastRefuel.km_contachilometri)} km percorsi da allora
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-green-400 font-mono text-2xl">
                    {formatCurrency(lastRefuel.importo, lastRefuel.valuta)}
                  </p>
                  <p className="text-[#9CA3AF] text-xs">Totale</p>
                </div>
                {lastRefuel.prezzo_litro && (
                  <div className="text-center">
                    <p className="text-white font-mono text-lg">
                      {lastRefuel.prezzo_litro.toFixed(3)}
                    </p>
                    <p className="text-[#9CA3AF] text-xs">{lastRefuel.valuta}/L</p>
                  </div>
                )}
                {lastRefuel.km_contachilometri && (
                  <div className="text-center">
                    <p className="text-[#FF5F1F] font-mono text-lg">
                      {formatNumber(lastRefuel.km_contachilometri)}
                    </p>
                    <p className="text-[#9CA3AF] text-xs">km</p>
                  </div>
                )}
                <Link 
                  to="/spese?tab=fuel-history"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors"
                >
                  Storico
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Moto List - Only show when no moto is selected */}
          {!selectedMoto && (
            <div className="vault-card" data-testid="moto-list-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-['Teko'] text-2xl text-white uppercase tracking-tight">
                  Le Tue Moto
                </h2>
                <Link 
                  to="/moto" 
                  className="text-[#FF5F1F] text-xs uppercase tracking-wider hover:underline flex items-center gap-1"
                >
                  Vedi tutte <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              {motoList.length === 0 ? (
                <div className="empty-state py-8">
                  <Bike className="empty-state-icon mx-auto" />
                  <p className="text-[#9CA3AF] mb-4">Nessuna moto registrata</p>
                  <Link 
                    to="/moto" 
                    className="text-[#FF5F1F] text-sm uppercase tracking-wider hover:underline"
                  >
                    Aggiungi la tua prima moto
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {motoList.slice(0, 3).map((m) => (
                    <Link
                      key={m.id}
                      to={`/moto/${m.id}`}
                      className="flex items-center gap-4 p-4 bg-[#121212] border border-[#262626] hover:border-[#FF5F1F]/50 transition-colors duration-150"
                      data-testid={`moto-item-${m.id}`}
                    >
                      {getMotoImageUrl(m) ? (
                        <img 
                          src={getMotoImageUrl(m)} 
                          alt=""
                          className="w-12 h-12 object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-[#FF5F1F]/10 flex items-center justify-center flex-shrink-0">
                          <Bike className="w-6 h-6 text-[#FF5F1F]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">
                          {m.marca} {m.modello}
                        </h3>
                        <p className="text-[#9CA3AF] text-sm font-mono">{m.targa}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#FF5F1F] font-mono text-lg">
                          {formatNumber(m.km_attuali)}
                        </p>
                        <p className="text-[#9CA3AF] text-xs uppercase tracking-wider">km</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Promemoria */}
          <div className={`vault-card ${selectedMoto ? 'lg:col-span-2' : ''}`} data-testid="promemoria-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Teko'] text-2xl text-white uppercase tracking-tight">
                Scadenze Imminenti
              </h2>
              <Link 
                to="/promemoria" 
                className="text-[#FF5F1F] text-xs uppercase tracking-wider hover:underline flex items-center gap-1"
              >
                Gestisci <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {promemoria.length === 0 && subscriptionReminders.length === 0 ? (
              <div className="empty-state py-8">
                <Bell className="empty-state-icon mx-auto" />
                <p className="text-[#9CA3AF] mb-4">Nessuna scadenza imminente</p>
                <Link 
                  to="/promemoria" 
                  className="text-[#FF5F1F] text-sm uppercase tracking-wider hover:underline"
                >
                  Aggiungi promemoria
                </Link>
              </div>
            ) : (
              <div className={`grid ${selectedMoto ? 'md:grid-cols-2' : ''} gap-3`}>
                {/* Subscription Renewal Reminders - Always show first */}
                {subscriptionReminders.map((reminder) => (
                  <div
                    key={reminder.id}
                    onClick={() => navigate('/abbonamento')}
                    className="flex items-center gap-4 p-4 border bg-[#FF5F1F]/10 border-[#FF5F1F] cursor-pointer hover:bg-[#FF5F1F]/20 transition-colors"
                    data-testid={`subscription-reminder-${reminder.id}`}
                  >
                    <Crown className="w-5 h-5 flex-shrink-0 text-[#FF5F1F]" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{reminder.title}</h3>
                      <p className="text-[#9CA3AF] text-xs">
                        {reminder.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm text-[#FF5F1F]">
                        {reminder.days_remaining === 0 ? 'Oggi' : `${reminder.days_remaining}g`}
                      </p>
                      <CreditCard className="w-4 h-4 text-[#FF5F1F] ml-auto" />
                    </div>
                  </div>
                ))}
                
                {/* Trial Status Banner */}
                {trialStatus?.status === 'trial' && (
                  <div
                    onClick={() => navigate('/abbonamento')}
                    className="flex items-center gap-4 p-4 border bg-blue-500/10 border-blue-500/50 cursor-pointer hover:bg-blue-500/20 transition-colors"
                  >
                    <Crown className="w-5 h-5 flex-shrink-0 text-blue-500" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium">Periodo di Prova</h3>
                      <p className="text-[#9CA3AF] text-xs">
                        {trialStatus.trial_days_remaining} giorni rimanenti
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm text-blue-500">
                        Gratis
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Regular Promemoria */}
                {promemoria.map((p) => {
                  const daysUntil = getDaysUntil(p.data_scadenza);
                  const isUrgent = daysUntil !== null && daysUntil <= 7;
                  const isOverdue = daysUntil !== null && daysUntil < 0;
                  
                  return (
                    <div
                      key={p.id}
                      className={`flex items-center gap-4 p-4 border ${
                        isOverdue 
                          ? 'bg-red-500/10 border-red-500/50' 
                          : isUrgent 
                            ? 'bg-yellow-500/10 border-yellow-500/50' 
                            : 'bg-[#121212] border-[#262626]'
                      }`}
                      data-testid={`promemoria-item-${p.id}`}
                    >
                      {(isUrgent || isOverdue) && (
                        <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                          isOverdue ? 'text-red-500' : 'text-yellow-500'
                        }`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">{p.titolo}</h3>
                        <p className="text-[#9CA3AF] text-xs uppercase tracking-wider">
                          {p.tipo}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-mono text-sm ${
                          isOverdue ? 'text-red-500' : isUrgent ? 'text-yellow-500' : 'text-[#FF5F1F]'
                        }`}>
                          {p.data_scadenza}
                        </p>
                        {daysUntil !== null && (
                          <p className={`text-xs ${
                            isOverdue ? 'text-red-500' : isUrgent ? 'text-yellow-500' : 'text-[#9CA3AF]'
                          }`}>
                            {isOverdue 
                              ? `Scaduto da ${Math.abs(daysUntil)} giorni`
                              : daysUntil === 0 
                                ? 'Scade oggi'
                                : `Tra ${daysUntil} giorni`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Image Lightbox */}
        {selectedMoto && selectedMoto.foto_urls && selectedMoto.foto_urls.length > 0 && (
          <ImageLightbox
            images={selectedMoto.foto_urls}
            initialIndex={currentPhotoIndex}
            isOpen={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </div>
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, mono, link, alert }) {
  const content = (
    <div className={`stat-card vault-card ${alert ? 'border-[#FF5F1F]' : ''}`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className={`w-5 h-5 ${alert ? 'text-[#FF5F1F]' : 'text-[#9CA3AF]'}`} />
        <span className="text-[#9CA3AF] text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-white text-2xl font-semibold ${mono ? 'font-mono' : "font-['Teko']"}`}>
        {value}
      </p>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block hover:scale-[1.02] transition-transform duration-150">
        {content}
      </Link>
    );
  }

  return content;
}
